import { FILE_VIEW_MODES, type FilesViewMode } from './files-url-state';

// How you like to LOOK at your files, remembered PER DEVICE — the third leg of a contract the two phones already
// keep: iOS in `UserDefaults` under `symphony.filesViewMode`, Android in SharedPreferences under the same name.
//
// The URL already carried the view so a refresh kept it and a link could pin it (`lib/files-url-state.ts`), but
// that is not the same thing: open `/files` fresh, or follow any link without a `view=`, and you were back to the
// list however many times you had chosen thumbnails. A preference has to outlive the URL that expressed it.
//
// **The URL still wins when it says something.** A shared link showing thumbnails must show thumbnails to whoever
// opens it, and it must not silently rewrite their own preference — so only an explicit toggle writes here.
//
// The key is named for the PRODUCT, not for this package: a new key has no existing browsers to keep compatible
// with, so it does not inherit the legacy `orion-web:` prefix (see the note in this package's CLAUDE.md).
export const FILES_VIEW_PREF_KEY = 'symphony-files-view';

/** The remembered view, or null when nothing is stored (or the stored value is not one we know). */
export function readFilesViewPref(storage: Pick<Storage, 'getItem'> | null = safeStorage()): FilesViewMode | null {
  const stored = storage?.getItem(FILES_VIEW_PREF_KEY);
  return FILE_VIEW_MODES.includes(stored as FilesViewMode) ? (stored as FilesViewMode) : null;
}

/** Remembers the view for this device. A failed write is not worth surfacing — the view still changed. */
export function writeFilesViewPref(view: FilesViewMode, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try {
    storage?.setItem(FILES_VIEW_PREF_KEY, view);
  } catch {
    // Private mode, a full quota, storage blocked by policy — none of which should break the toggle.
  }
}

/** `localStorage`, or null where there isn't one: this module is imported by code that renders on the server, and
 *  a Safari private-mode window throws on the mere access rather than on the read. */
function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
